/**
 * Deployment helper (house-rule model, 2026-07-02). Under one-ship-per-tile a
 * fleet can't legally start stacked, but ships are placed directly (buy screen,
 * AI buy, instant battles) without going through canStop. separateShips() runs
 * once after placement to guarantee the invariant: any tile holding more than
 * one ship keeps the lowest-id ship and relocates the rest to the nearest empty
 * water tile via a deterministic spiral BFS (no RNG — safe for seeded replay).
 */
import { neighbors } from './board';
import { type GameState } from './state';

/** Stamp every city's tile as impassable land. mapgen already picks city tiles
 *  from coastal LAND, but the hand-built tutorial / instant maps place cities on
 *  open sea, so they call this to make those tiles land before separateShips. */
export function markCityTiles(g: GameState): void {
  for (const ct of g.cities) if (g.tiles[ct.r]) g.tiles[ct.r]![ct.c] = 'land';
}

function tileFree(g: GameState, c: number, r: number, taken: Set<string>): boolean {
  if (c < 0 || r < 0 || c >= g.W || r >= g.H) return false;
  if (g.tiles[r]![c] !== 'water') return false; // land excluded
  return !taken.has(`${c},${r}`);
}

/** Nearest free water tile to (c0,r0) by HEX distance (BFS over hex neighbours, so
 *  a ship relocated off a land city lands on an ADJACENT water tile — it must stay
 *  hex-adjacent to hold the city). Explores through land/occupied tiles to reach
 *  open water beyond. Falls back to the origin if the sea is impossibly full. */
function nearestFree(g: GameState, c0: number, r0: number, taken: Set<string>): [number, number] {
  const seen = new Set<string>([`${c0},${r0}`]);
  let frontier: [number, number][] = [[c0, r0]];
  while (frontier.length) {
    const next: [number, number][] = [];
    for (const [c, r] of frontier) {
      for (const [nc, nr] of neighbors(g, c, r)) {
        const k = `${nc},${nr}`;
        if (seen.has(k)) continue;
        seen.add(k);
        if (tileFree(g, nc, nr, taken)) return [nc, nr];
        next.push([nc, nr]);
      }
    }
    frontier = next;
  }
  return [c0, r0];
}

/** Enforce one ship per WATER tile after direct placement. Deterministic. A ship
 *  placed on a non-water tile (a home city is now impassable land) or on a tile
 *  another ship already claimed is relocated to the nearest free water — so a
 *  fleet added "at" its home ports spreads onto the adjacent sea. */
export function separateShips(g: GameState): void {
  const taken = new Set<string>();
  // lowest id first → the earliest-placed ship keeps its tile
  const ordered = [...g.ships].sort((a, b) => a.id - b.id);
  for (const s of ordered) {
    const k = `${s.c},${s.r}`;
    if (g.tiles[s.r]?.[s.c] === 'water' && !taken.has(k)) {
      taken.add(k);
      continue;
    }
    const [c, r] = nearestFree(g, s.c, s.r, taken);
    s.c = c;
    s.r = r;
    taken.add(`${c},${r}`);
  }
}
